import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Select } from '@/shared/ui/primitives';
import { ReloadOutlined } from '@/shared/ui/icons';
import type { GrowthAnalyticsParams } from '../api/analytics.api';
import { useAnalyticsGeoCities, useAnalyticsGeoDistricts } from '../hooks/useAnalyticsQueries';

type Props = {
  params: GrowthAnalyticsParams;
  setParams: (params: GrowthAnalyticsParams) => void;
  onRefresh: () => void;
  loading?: boolean;
};

export default function AnalyticsFilterBar({ params, setParams, onRefresh, loading }: Props) {
  const { t } = useTranslation();
  const cities = useAnalyticsGeoCities();
  const districts = useAnalyticsGeoDistricts(params.cityId);

  const cityOptions = useMemo(() => [
    { value: '', label: t('analytics.filters.allCities') },
    ...(Array.isArray(cities.data) ? cities.data : []).map((city: any) => ({ value: city.id, label: city.name })),
  ], [cities.data, t]);

  const districtOptions = useMemo(() => [
    { value: '', label: t('analytics.filters.allDistricts') },
    ...(Array.isArray(districts.data) ? districts.data : []).map((district: any) => ({ value: district.id, label: district.name })),
  ], [districts.data, t]);

  const changeCity = (value: any) => {
    setParams({
      ...params,
      cityId: value || '',
      districtId: '',
    });
  };

  return (
    <Card className="analytics-filter-card" styles={{ body: { padding: 16 } }} style={{ marginBottom: 16 }}>
      <div className="analytics-filter-header">
        <div>
          <strong>{t('analytics.filters.title')}</strong>
          <div className="ds-muted">{t('analytics.filters.subtitle')}</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>{t('dashboard.refresh')}</Button>
      </div>
      <div className="analytics-filter-grid">
        <label className="analytics-filter-field">
          <span>{t('common.from')}</span>
          <Input type="date" value={params.from ?? ''} onChange={(e: any) => setParams({ ...params, from: e.target.value })} />
        </label>
        <label className="analytics-filter-field">
          <span>{t('common.to')}</span>
          <Input type="date" value={params.to ?? ''} onChange={(e: any) => setParams({ ...params, to: e.target.value })} />
        </label>
        <label className="analytics-filter-field">
          <span>{t('analytics.common.platform')}</span>
          <Select
            value={params.platform ?? ''}
            onChange={(value) => setParams({ ...params, platform: value || '' })}
            options={[
              { value: '', label: t('analytics.filters.allPlatforms') },
              { value: 'ios', label: 'iOS' },
              { value: 'android', label: 'Android' },
              { value: 'web', label: 'Web' },
            ]}
          />
        </label>
        <label className="analytics-filter-field">
          <span>{t('common.city')}</span>
          <Select
            showSearch
            value={params.cityId ?? ''}
            loading={cities.isLoading || cities.isFetching}
            onChange={changeCity}
            options={cityOptions}
            placeholder={t('analytics.filters.allCities')}
          />
        </label>
        <label className="analytics-filter-field">
          <span>{t('common.district')}</span>
          <Select
            showSearch
            value={params.districtId ?? ''}
            loading={districts.isLoading || districts.isFetching}
            disabled={!params.cityId}
            onChange={(value) => setParams({ ...params, districtId: value || '' })}
            options={districtOptions}
            placeholder={params.cityId ? t('analytics.filters.allDistricts') : t('analytics.filters.selectCityFirst')}
          />
        </label>
        <label className="analytics-filter-field">
          <span>{t('analytics.common.adType')}</span>
          <Select
            value={params.adType ?? ''}
            onChange={(value) => setParams({ ...params, adType: value || '' })}
            options={[
              { value: '', label: t('analytics.filters.allAdTypes') },
              { value: 'room_ad', label: t('analytics.common.roomAds') },
              { value: 'room_wanted', label: t('analytics.common.roomWanted') },
            ]}
          />
        </label>
      </div>
    </Card>
  );
}
